import React, { useRef, forwardRef, useImperativeHandle, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingListItem, PositionSuggestionResponse } from '../../../../apiServices/types';
import { DeleteIcon } from '../../../UI/Icons';
import { evaluateMathExpression } from '../../../../utils';
import PositionSuggestionsPanel from './PositionSuggestionsPanel';

export interface CategoryItemRef {
  focusPrice: () => void;
}

interface CategoryItemProps {
  item: ShoppingListItem;
  priceMode: boolean;
  onToggle: () => void;
  onUpdatePrice: (price: string) => void;
  onUpdateName?: (name: string) => void;
  onSavePrice?: () => void;
  onRemove: () => void;
  onPriceEnter?: () => void; // Callback коли натискають Enter в інпуті ціни
  categoryId?: string; // ID категорії для хінтів
}

const CategoryItem = forwardRef<CategoryItemRef, CategoryItemProps>(({
  item,
  priceMode,
  onToggle,
  onUpdatePrice,
  onUpdateName,
  onSavePrice,
  onRemove,
  onPriceEnter,
  categoryId
}, ref) => {
  const initialPriceRef = useRef<number | null>(null);
  const priceInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState(item.name);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState<string | null>(null);
  const [inputStyles, setInputStyles] = useState<React.CSSProperties>({});
  const [priceExpression, setPriceExpression] = useState<string>('');
  const [isPriceInputFocused, setIsPriceInputFocused] = useState(false);
  const [calcHintPosition, setCalcHintPosition] = useState<{ top: number; left: number; isBelow: boolean }>({ top: 0, left: 0, isBelow: false });
  
  // Обчислюємо результат математичного виразу
  const mathResult = useMemo(() => {
    if (!priceExpression) return { result: null, isExpression: false };
    return evaluateMathExpression(priceExpression);
  }, [priceExpression]);
  
  // Оновлення позиції тултіпа калькуляції
  useEffect(() => {
    if (!isPriceInputFocused || !priceInputRef.current || !mathResult.isExpression || mathResult.result === null) {
      return;
    }
    
    const updatePosition = () => {
      if (!priceInputRef.current) return;
      
      const inputRect = priceInputRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      const viewportWidth = window.innerWidth;
      
      // Приблизна ширина тултіпа (= + число + padding)
      const tooltipWidth = 60;
      const spacing = 6;
      
      // Перевіряємо чи влазить справа
      const fitsRight = inputRect.right + spacing + tooltipWidth <= viewportWidth;
      
      if (fitsRight) {
        // Позиціонуємо справа від інпута
        setCalcHintPosition({
          top: inputRect.top + scrollY + inputRect.height / 2,
          left: inputRect.right + scrollX + spacing,
          isBelow: false
        });
      } else {
        // Позиціонуємо під інпутом
        setCalcHintPosition({
          top: inputRect.bottom + scrollY + 4,
          left: inputRect.left + scrollX,
          isBelow: true
        });
      }
    };
    
    updatePosition();
    
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isPriceInputFocused, mathResult.isExpression, mathResult.result]);

  useImperativeHandle(ref, () => ({
    focusPrice: () => {
      priceInputRef.current?.focus();
    }
  }));

  const handleFocus = (): void => {
    initialPriceRef.current = item.price || 0;
    setIsPriceInputFocused(true);
    // Ініціалізуємо вираз поточним значенням
    setPriceExpression(item.price ? String(item.price) : '');
  };

  const applyMathResult = (): void => {
    // Якщо є результат обчислення, застосовуємо його
    if (mathResult.result !== null) {
      onUpdatePrice(String(mathResult.result));
      setPriceExpression('');
    }
  };

  const handleBlur = (): void => {
    setIsPriceInputFocused(false);
    
    // Застосовуємо результат обчислення якщо є
    applyMathResult();
    
    if (onSavePrice && initialPriceRef.current !== null) {
      const currentPrice = item.price || 0;
      // Зберігаємо тільки якщо значення змінилося
      if (currentPrice !== initialPriceRef.current) {
        onSavePrice();
      }
      initialPriceRef.current = null;
    }
    
    setPriceExpression('');
  };

  const handlePriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Застосовуємо результат обчислення якщо є
      applyMathResult();
      
      if (onSavePrice) {
        onSavePrice();
      }
      if (onPriceEnter) {
        onPriceEnter();
      }
    }
  };
  
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setPriceExpression(value);
    
    // Якщо це просто число, оновлюємо ціну
    const evalResult = evaluateMathExpression(value);
    if (!evalResult.isExpression) {
      onUpdatePrice(value);
    }
  };

  useEffect(() => {
    setEditingNameValue(item.name);
  }, [item.name]);

  const handleStartEditName = () => {
    setIsEditingName(true);
    setEditingNameValue(item.name);
  };

  const handleSaveName = () => {
    if (!editingNameValue.trim()) {
      return;
    }
    if (onUpdateName && editingNameValue.trim() !== item.name) {
      onUpdateName(editingNameValue.trim());
    }
    setIsEditingName(false);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditingNameValue(item.name);
  };

  const handleSelectSuggestion = (value: string) => {
    setEditingNameValue(value);
    // Зберігаємо назву після вибору саджеста
    if (onUpdateName && value.trim() !== item.name) {
      onUpdateName(value.trim());
    }
    setIsEditingName(false);
  };

  const handleHighlightChange = (suggestion: PositionSuggestionResponse | null) => {
    setHighlightedSuggestion(suggestion ? suggestion.name : null);
  };

  // Обчислити текст для оверлею (тільки частина після введеного тексту)
  const getOverlayText = () => {
    if (!highlightedSuggestion || !editingNameValue || editingNameValue.trim().length === 0) return null;
    const lowerItemName = editingNameValue.toLowerCase().trim();
    const lowerSuggestion = highlightedSuggestion.toLowerCase();
    
    // Знайти позицію, з якої починається відмінність
    if (lowerSuggestion.startsWith(lowerItemName) && lowerItemName.length > 0) {
      return highlightedSuggestion.substring(editingNameValue.trim().length);
    }
    return null;
  };

  const overlayText = getOverlayText();

  // Отримати стилі з інпута для точного збігу
  useEffect(() => {
    if (!isEditingName || !nameInputRef.current) return;

    const updateStyles = () => {
      if (nameInputRef.current) {
        const computedStyle = window.getComputedStyle(nameInputRef.current);
        setInputStyles({
          fontFamily: computedStyle.fontFamily,
          fontSize: computedStyle.fontSize,
          fontStyle: computedStyle.fontStyle,
          fontWeight: computedStyle.fontWeight,
          letterSpacing: computedStyle.letterSpacing,
          lineHeight: computedStyle.lineHeight,
          padding: computedStyle.padding,
          paddingLeft: computedStyle.paddingLeft,
          paddingRight: computedStyle.paddingRight,
          paddingTop: computedStyle.paddingTop,
          paddingBottom: computedStyle.paddingBottom,
          borderLeftWidth: computedStyle.borderLeftWidth,
          borderRightWidth: computedStyle.borderRightWidth,
          borderTopWidth: computedStyle.borderTopWidth,
          borderBottomWidth: computedStyle.borderBottomWidth,
          height: computedStyle.height,
        });
      }
    };

    updateStyles();
    
    // Використати ResizeObserver для відстеження змін розміру
    const resizeObserver = new ResizeObserver(() => {
      updateStyles();
    });
    
    resizeObserver.observe(nameInputRef.current);
    
    // Оновити стилі при зміні розміру вікна
    window.addEventListener('resize', updateStyles);
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateStyles);
    };
  }, [isEditingName]);

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEditName();
    }
  };

  return (
    <div className="item">
      <div
        className={`item-checkbox ${item.checked ? 'checked' : ''}`}
        onClick={onToggle}
      ></div>
      {isEditingName ? (
        <div className="item-name-edit" style={{ position: 'relative', flex: 1 }}>
          <input
            ref={nameInputRef}
            type="text"
            className="item-name-input"
            value={editingNameValue}
            onChange={(e) => setEditingNameValue(e.target.value)}
            onKeyDown={handleNameKeyDown}
            onBlur={handleSaveName}
            autoFocus
            autoComplete="off"
          />
          {overlayText && nameInputRef.current && Object.keys(inputStyles).length > 0 && (
            <span 
              className="add-item-input-overlay"
              style={{
                position: 'absolute',
                left: '2px',
                top: 0,
                pointerEvents: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                color: 'var(--primary)',
                opacity: 0.7,
                whiteSpace: 'nowrap',
                boxSizing: 'border-box',
                overflow: 'hidden',
                width: '100%',
                fontFamily: inputStyles.fontFamily,
                fontSize: inputStyles.fontSize,
                fontStyle: inputStyles.fontStyle,
                fontWeight: inputStyles.fontWeight,
                letterSpacing: inputStyles.letterSpacing,
                lineHeight: inputStyles.lineHeight,
                padding: inputStyles.padding,
                paddingLeft: inputStyles.paddingLeft,
                paddingRight: inputStyles.paddingRight,
                paddingTop: inputStyles.paddingTop,
                paddingBottom: inputStyles.paddingBottom,
                height: inputStyles.height,
              }}
            >
              <span style={{ visibility: 'hidden', whiteSpace: 'pre' }}>{editingNameValue}</span>
              <span>{overlayText}</span>
            </span>
          )}
          {onUpdateName && (
            <PositionSuggestionsPanel
              value={editingNameValue}
              categoryId={categoryId}
              onSelect={handleSelectSuggestion}
              onHighlightChange={handleHighlightChange}
              inputRef={nameInputRef}
            />
          )}
        </div>
      ) : (
        <span 
          className={`item-name ${item.checked ? 'checked' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (onUpdateName) {
              handleStartEditName();
            }
          }}
          title={onUpdateName ? 'Натисніть для редагування' : ''}
          style={{ cursor: onUpdateName ? 'pointer' : 'default' }}
        >
          {item.name}
        </span>
      )}
      <div className="item-price" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <input
          ref={priceInputRef}
          type="text"
          className="price-input"
          placeholder="0.00"
          value={isPriceInputFocused ? priceExpression : (item.price || '')}
          onChange={handlePriceChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handlePriceKeyDown}
        />
        {isPriceInputFocused && mathResult.isExpression && mathResult.result !== null && createPortal(
          <div 
            className={`price-calc-tooltip ${calcHintPosition.isBelow ? 'below' : ''}`}
            style={{
              position: 'absolute',
              top: calcHintPosition.top,
              left: calcHintPosition.left,
              transform: calcHintPosition.isBelow ? 'none' : 'translateY(-50%)',
              zIndex: 9999999
            }}
          >
            <span className="price-calc-tooltip-content">
              = {mathResult.result}
            </span>
          </div>,
          document.body
        )}
      </div>
      <button 
        className="category-delete-btn" 
        onClick={onRemove} 
        title="Видалити"
      >
        <DeleteIcon size={16} />
      </button>
    </div>
  );
});

CategoryItem.displayName = 'CategoryItem';

export default CategoryItem;
